import { Canvas } from "@react-three/fiber";
import { useEffect, type JSX } from "react";
import {
  fetchBaseBuildings,
  fetchConstructionList,
  fetchHealth,
  fetchProductionList,
  fetchResearchList,
  getMe,
} from "./api.js";
import { ContinuousZoomCamera } from "./camera/ContinuousZoomCamera.js";
import { useZoomLayerName } from "./camera/zoom.js";
import { BottomBar } from "./hud/BottomBar.js";
import { BuildingInfoPopup } from "./hud/BuildingInfoPopup.js";
import { DiagnosticsToggle } from "./hud/DiagnosticsToggle.js";
import { OperationsDashboard } from "./hud/OperationsDashboard.js";
import { ProductionPanel } from "./hud/ProductionPanel.js";
import { ResearchPanel } from "./hud/ResearchPanel.js";
import { SpeedUpConfirmModal } from "./hud/SpeedUpConfirmModal.js";
import { TopBar } from "./hud/TopBar.js";
import { UpgradeConfirmModal } from "./hud/UpgradeConfirmModal.js";
import { BaseScene } from "./scenes/BaseScene.js";
import { WorldScene } from "./scenes/WorldScene.js";
import { AuthScreen } from "./screens/AuthScreen.js";
import { ServerBrowserScreen } from "./screens/ServerBrowserScreen.js";
import { useStore } from "./store.js";
import { connectWs, disconnectWs } from "./ws.js";

const LAYER_LABEL: Record<string, string> = {
  BASE: "BASE VIEW",
  REGIONAL: "REGIONAL",
  WORLD: "WORLD MAP",
  TACTICAL: "TACTICAL",
};

function GameView(): JSX.Element {
  const zoom = useStore((s) => s.zoom);
  const layer = useZoomLayerName(zoom);
  const me = useStore((s) => s.me);
  const setMe = useStore((s) => s.setMe);
  const setBuildings = useStore((s) => s.setBuildings);
  const setProductionJobs = useStore((s) => s.setProductionJobs);
  const setConstructionJobs = useStore((s) => s.setConstructionJobs);
  const setResearchJobs = useStore((s) => s.setResearchJobs);
  const setCompletedNodeIds = useStore((s) => s.setCompletedNodeIds);
  const setResearchSlots = useStore((s) => s.setResearchSlots);
  const setSelectedBuildingId = useStore((s) => s.setSelectedBuildingId);
  const setProductionPanelOpen = useStore((s) => s.setProductionPanelOpen);
  const setProductionPanelBuildingId = useStore((s) => s.setProductionPanelBuildingId);
  const setResearchPanelOpen = useStore((s) => s.setResearchPanelOpen);

  // On game entry: fetch buildings + production + construction, subscribe to live frames.
  useEffect(() => {
    if (!me) return;
    let cancelled = false;
    const refreshBuildings = async (): Promise<void> => {
      try {
        const res = await fetchBaseBuildings(me.base.id);
        if (!cancelled) setBuildings(res.buildings);
      } catch (err) {
        console.warn("[stahlfront] base buildings probe failed:", err);
      }
    };
    const refreshConstruction = async (): Promise<void> => {
      try {
        const res = await fetchConstructionList(me.base.id);
        if (!cancelled) setConstructionJobs(res.jobs);
      } catch (err) {
        console.warn("[stahlfront] construction probe failed:", err);
      }
    };
    const refreshResearch = async (): Promise<void> => {
      try {
        const res = await fetchResearchList();
        if (!cancelled) {
          setResearchJobs(res.activeJobs);
          setCompletedNodeIds(res.completedNodeIds);
          setResearchSlots(res.slots);
        }
      } catch (err) {
        console.warn("[stahlfront] research probe failed:", err);
      }
    };

    void refreshBuildings();
    void fetchProductionList(me.base.id)
      .then((res) => {
        if (!cancelled) setProductionJobs(res.jobs);
      })
      .catch((err) => console.warn("[stahlfront] production list probe failed:", err));
    void refreshConstruction();
    void refreshResearch();

    connectWs(me.player.serverId);

    // Keep construction + research lists live: poll 1Hz while jobs are in flight (cheap).
    const pollId = setInterval(() => {
      void refreshConstruction();
      void refreshResearch();
    }, 1000);
    const onUpgraded = (): void => {
      void refreshBuildings();
      void refreshConstruction();
      void refreshResearch();
      void getMe().then((fresh) => {
        if (!cancelled && fresh) setMe(fresh);
      });
    };
    const onResearchComplete = (): void => {
      void refreshResearch();
      void getMe().then((fresh) => {
        if (!cancelled && fresh) setMe(fresh);
      });
    };
    window.addEventListener("stahlfront:building.upgraded", onUpgraded);
    window.addEventListener("stahlfront:research.complete", onResearchComplete);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      window.removeEventListener("stahlfront:building.upgraded", onUpgraded);
      window.removeEventListener("stahlfront:research.complete", onResearchComplete);
      disconnectWs();
    };
  }, [
    me,
    setBuildings,
    setProductionJobs,
    setConstructionJobs,
    setResearchJobs,
    setCompletedNodeIds,
    setResearchSlots,
    setMe,
  ]);

  const onOpenProduction = (buildingId: string): void => {
    setProductionPanelBuildingId(buildingId);
    setProductionPanelOpen(true);
    setSelectedBuildingId(null);
  };
  const onOpenResearch = (): void => {
    setResearchPanelOpen(true);
    setSelectedBuildingId(null);
  };

  return (
    <div className="app-shell">
      <div className="canvas-host">
        <Canvas
          dpr={[1, 2]}
          gl={{ antialias: true, powerPreference: "high-performance" }}
          onPointerMissed={() => setSelectedBuildingId(null)}
        >
          <color attach="background" args={["#0c0a08"]} />
          <fog attach="fog" args={["#0c0a08", 60, 360]} />
          <ambientLight intensity={0.45} />
          <directionalLight position={[12, 18, 10]} intensity={1.0} castShadow />
          <directionalLight position={[-8, 6, -10]} intensity={0.35} color="#7ea5d4" />
          <ContinuousZoomCamera />
          <BaseScene />
          <WorldScene />
        </Canvas>
      </div>
      <div className="hud">
        <TopBar />
        <div />
        <BottomBar />
      </div>
      <div className="layer-label">{LAYER_LABEL[layer] ?? layer}</div>
      <DiagnosticsToggle />
      <OperationsDashboard />
      <BuildingInfoPopup
        onOpenProduction={onOpenProduction}
        onOpenResearch={onOpenResearch}
      />
      <ProductionPanel />
      <ResearchPanel />
      <UpgradeConfirmModal />
      <SpeedUpConfirmModal />
    </div>
  );
}

export default function App(): JSX.Element {
  const screen = useStore((s) => s.currentScreen);
  const setCurrentScreen = useStore((s) => s.setCurrentScreen);
  const setMe = useStore((s) => s.setMe);

  useEffect(() => {
    fetchHealth().catch((err) => {
      console.warn("[stahlfront] /api/health probe failed:", err);
    });
    getMe()
      .then((me) => {
        if (me) {
          setMe(me);
          setCurrentScreen("game");
        }
      })
      .catch((err) => {
        console.warn("[stahlfront] /api/auth/me probe failed:", err);
      });
  }, [setCurrentScreen, setMe]);

  if (screen === "server-browser") return <ServerBrowserScreen />;
  if (screen === "auth") return <AuthScreen />;
  return <GameView />;
}
